import { Cell, Grid, Row, Separator, useOverlay, H3, H2, Button } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { removeRelationship, restoreRelationship } from '../../../services/personService'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import { CtrLink, PersonLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DmcConclusionFact } from '../../common/conclusionFact/DmcConclusionFact'
import { DmcNewConclusionFact } from '../../common/conclusionFact/DmcNewConclusionFact'
import DataView from '../../common/DataView'
import { DotSeparator } from '../../metadata/MetaData'
import ConfirmRemoveDialog from '../../person/relationships/ConfirmRemoveDialog'
import RestoreRelationshipsDialog from '../../person/relationships/RestoreRelationshipsDialog'
import { GetAssociationRelationshipRole, RelationshipContext } from '../relationshipUtils'
import RemoveRelationshipPersonDialog from '../../person/relationships/RemoveRelationshipPersonDialog'
import { editParentChildRelationship, switchPersons } from '../../../services/RelationshipService'
import ChangeRelationshipPersonDialog from '../../person/relationships/ChangeRelationshipPersonDialog'
import SwitchRelationshipParentsDialog from '../../person/relationships/SwitchRelationshipParents'
import SwitchRelationshipSpousesDialog from '../../person/relationships/SwitchRelationshipSpouses'
import SwitchRelationshipPersonsDialog from '../../person/relationships/SwitchRelationshipPersons'

export default function RelationshipSummary() {
  usePageView({ page_detail: 'DMConsole:RelationshipSummary' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.relationship.summary'))
  const { relationshipState, relationshipActions } = useContext(RelationshipContext)

  if (!relationshipState.relationshipData) {
    return null
  }

  if (relationshipState.relationshipData.associationType) {
    return <AssociationRelationship relationshipState={relationshipState} relationshipActions={relationshipActions} />
  }
  if (relationshipState.relationshipData.child) {
    return <ParentChildRelationship relationshipState={relationshipState} relationshipActions={relationshipActions} />
  }
  return <CoupleRelationship relationshipState={relationshipState} relationshipActions={relationshipActions} />
}

function AssociationRelationship({ relationshipState, relationshipActions }) {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const removeDialogOpener = useOverlay()
  const restoreDialogOpener = useOverlay()
  const switchPersonsDialogOpener = useOverlay()
  const { isRole } = useContext(PermissionsContext)
  const isTraditionalCouple = (person1, person2) => {
    return person1.gender === 'MALE' && person2.gender === 'FEMALE'
  }
  const allowSwitchingPersons = (person1, person2) => {
    return isRole(['Data Admin 6']) && !isTraditionalCouple(person1, person2)
  }

  const adviceToAllowSwitchingParents = (person1, person2) => {
    return isTraditionalCouple(person1, person2)
      ? t('person.relationships.cannotSwitch', 'You cannot switch if person1 is male and person2 is female')
      : ''
  }
  const handleRemoveRelationship = (reason) => {
    removeRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        removeDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        removeDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleRestoreRelationship = (reason) => {
    restoreRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        restoreDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        restoreDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleSwitchPersons = (reason) => {
    switchPersons(
      'association',
      relationshipState.relationshipData.relationshipId,
      reason,
      relationshipState.relationshipData.person2.personId,
      relationshipState.relationshipData.person1.personId
    )
      .then(() => {
        switchPersonsDialogOpener.close()
        window.location.reload()
      })
      .catch((err) => {
        switchPersonsDialogOpener.close()
        error(
          t('person.relationships.errors.switchingPerson', {
            defaultValue: `Error switching persons in the association: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  return (
    <>
      <Row>
        <H2 size="H6" growX supplementary>
          {t(
            `relationship.type.${relationshipState.relationshipData.associationType}`,
            `Relationship: ${relationshipState.relationshipData.associationType}`
          )}
          <DotSeparator />
          <DmcCopyToClipboardTooltip value={relationshipState.relationshipData.relationshipId} />
          {relationshipState.relationshipData.tombstoned && (
            <>
              <DotSeparator />
              {t('tombstoned', 'Tombstoned')}
            </>
          )}
        </H2>
        {relationshipState.relationshipData.tombstoned && (
          <Button
            emphasis="medium"
            onClick={restoreDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.restore', 'Restore')}
          </Button>
        )}
        {isRole(['Data Admin 6']) && (
          <Button
            emphasis="medium"
            onClick={switchPersonsDialogOpener.handleClick}
            disabled={
              !allowSwitchingPersons(
                relationshipState.relationshipData.person1,
                relationshipState.relationshipData.person2
              )
            }
            title={adviceToAllowSwitchingParents(
              relationshipState.relationshipData.person1,
              relationshipState.relationshipData.person2
            )}
          >
            {t('relationship.action.switchPersons', 'Switch Persons')}
          </Button>
        )}
        {!relationshipState.relationshipData.tombstoned && (
          <Button
            emphasis="medium"
            onClick={removeDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.tombstone', 'Tombstone')}
          </Button>
        )}
      </Row>
      <Separator size="xs" />
      <RelationshipPersonSummary
        person={relationshipState.relationshipData.person1}
        role={GetAssociationRelationshipRole(true, relationshipState.relationshipData.associationType)}
      />
      <Separator size="sm" />
      <RelationshipPersonSummary
        person={relationshipState.relationshipData.person2}
        role={GetAssociationRelationshipRole(false, relationshipState.relationshipData.associationType)}
      />
      <Separator size="sm" />
      <RelationshipFactsSummary relationshipState={relationshipState} relationshipActions={relationshipActions} />
      <ConfirmRemoveDialog
        {...removeDialogOpener}
        onClose={removeDialogOpener.close}
        onRemove={handleRemoveRelationship}
      />
      <RestoreRelationshipsDialog
        {...restoreDialogOpener}
        onClose={restoreDialogOpener.close}
        onRestore={handleRestoreRelationship}
      />
      <SwitchRelationshipPersonsDialog
        relationship={relationshipState.relationshipData}
        {...switchPersonsDialogOpener}
        onClose={switchPersonsDialogOpener.close}
        onOk={handleSwitchPersons}
      />
    </>
  )
}

function ParentChildRelationship({ relationshipState, relationshipActions }) {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const { error, warn } = useNotifications()
  const [personToRemove, setPersonToRemove] = useState({})
  const [personToChange, setPersonToChange] = useState({})
  const removePersonDialogOpener = useOverlay()
  const changePersonDialogOpener = useOverlay()
  const removeDialogOpener = useOverlay()
  const restoreDialogOpener = useOverlay()
  const switchParentsDialogOpener = useOverlay()
  const history = useHistory()

  const { isRole } = useContext(PermissionsContext)
  const isTraditionalCouple = (person1, person2) => {
    return person1?.gender === 'MALE' && person2?.gender === 'FEMALE'
  }
  const allowSwitchingPersons = (person1, person2) => {
    return isRole(['Data Admin 6']) && !isTraditionalCouple(person1, person2)
  }

  const adviceToAllowSwitchingParents = (person1, person2) => {
    return isTraditionalCouple(person1, person2)
      ? t('person.relationships.cannotSwitch', 'You cannot switch if person1 is male and person2 is female')
      : ''
  }
  const allowRemove =
    relationshipState.relationshipData.parent1?.personId && relationshipState.relationshipData.parent2?.personId

  const handleRemovePerson = (person) => {
    setPersonToRemove(person)
    removePersonDialogOpener.handleClick()
  }

  const handleChangePerson = (person) => {
    setPersonToChange(person)
    changePersonDialogOpener.handleClick()
  }

  const handleSwitchParents = (reason) => {
    switchPersons(
      'parentchild',
      relationshipState.relationshipData.relationshipId,
      reason,
      relationshipState.relationshipData.parent2.personId,
      relationshipState.relationshipData.parent1.personId
    )
      .then(() => {
        switchParentsDialogOpener.close()
        window.location.reload()
      })
      .catch((err) => {
        switchParentsDialogOpener.close()
        error(
          t('person.relationships.errors.switchingPerson', {
            defaultValue: `Error switching persons in the relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleEditParentChildRelationship = (reason, person1Id, person2Id, childId) => {
    editParentChildRelationship(
      relationshipState.relationshipData.relationshipId,
      reason,
      person1Id,
      person2Id,
      childId
    )
      .then((response) => {
        removePersonDialogOpener.close()
        changePersonDialogOpener.close()
        const relationshipId = response.headers['x-entity-id']
        const warning = response.headers.warning
        if (warning) {
          warn(warning)
        }
        history.push(`/relationship/parentchild/${relationshipId}/summary`)
      })
      .catch((err) => {
        removePersonDialogOpener.close()
        changePersonDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleRemoveRelationship = (reason) => {
    removeRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        removeDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        removeDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleRestoreRelationship = (reason) => {
    restoreRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        restoreDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        restoreDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  return (
    <>
      <Row>
        <H2 size="H6" growX supplementary>
          {t(`relationship.type.parentchild`, `Relationship: Family`)}
          <DotSeparator />
          <DmcCopyToClipboardTooltip value={relationshipState.relationshipData.relationshipId} />
          {relationshipState.relationshipData.tombstoned && (
            <>
              <DotSeparator />
              {t('tombstoned', 'Tombstoned')}
            </>
          )}
        </H2>

        {isRole(['Data Admin 6']) && (
          <Button
            emphasis="medium"
            onClick={switchParentsDialogOpener.handleClick}
            disabled={
              !allowSwitchingPersons(
                relationshipState.relationshipData.parent1,
                relationshipState.relationshipData.parent2
              )
            }
            title={adviceToAllowSwitchingParents(
              relationshipState.relationshipData.parent1,
              relationshipState.relationshipData.parent2
            )}
          >
            {t('relationship.action.switchParents', 'Switch Parents')}
          </Button>
        )}
        {relationshipState.relationshipData.tombstoned && (
          <Button
            emphasis="medium"
            onClick={restoreDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.restore', 'Restore')}
          </Button>
        )}
        {!relationshipState.relationshipData.tombstoned && (
          <Button
            id="tombstoneParentRelationshipBtn"
            emphasis="medium"
            onClick={removeDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.tombstone', 'Tombstone')}
          </Button>
        )}
      </Row>
      <Separator size="xs" />
      <RelationshipPersonSummaryWithButtons
        person={relationshipState.relationshipData.parent1}
        role={t('relationship.role.parent', 'Parent')}
        isAuthorized={isAuthorized('FtWritePermission')}
        removeButtonLabel={t('action.remove', 'Remove')}
        removeOnClick={() => handleRemovePerson(relationshipState.relationshipData.parent1)}
        changeButtonLabel={t('action.change', 'Change')}
        changeButtonClick={() => handleChangePerson(relationshipState.relationshipData.parent1)}
      />
      <Separator size="sm" />
      <RelationshipPersonSummaryWithButtons
        person={relationshipState.relationshipData.parent2}
        role={t('relationship.role.parent', 'Parent')}
        isAuthorized={isAuthorized('FtWritePermission')}
        removeButtonLabel={allowRemove ? t('action.remove', 'Remove') : null}
        removeOnClick={() => handleRemovePerson(relationshipState.relationshipData.parent2)}
        changeButtonLabel={t('action.change', 'Change')}
        changeButtonClick={() => handleChangePerson(relationshipState.relationshipData.parent2)}
      />
      <Separator size="sm" />
      <RelationshipPersonSummary person={relationshipState.relationshipData.child} role={t('child', 'Child')} />
      <Separator size="sm" />
      <RelationshipFactsSummary relationshipState={relationshipState} relationshipActions={relationshipActions} />
      <RelationshipOrdinancesSummary relationshipState={relationshipState} />
      <ChangeRelationshipPersonDialog
        {...changePersonDialogOpener}
        personToChange={personToChange}
        parent1Id={relationshipState.relationshipData.parent1?.personId}
        parent2Id={relationshipState.relationshipData.parent2?.personId}
        childId={relationshipState.relationshipData.child?.personId}
        onEdit={handleEditParentChildRelationship}
      />
      <RemoveRelationshipPersonDialog
        {...removePersonDialogOpener}
        personToRemove={personToRemove}
        parent1Id={relationshipState.relationshipData.parent1?.personId}
        parent2Id={relationshipState.relationshipData.parent2?.personId}
        childId={relationshipState.relationshipData.child?.personId}
        onEdit={handleEditParentChildRelationship}
      />
      <ConfirmRemoveDialog
        {...removeDialogOpener}
        onClose={removeDialogOpener.close}
        onRemove={handleRemoveRelationship}
      />
      <RestoreRelationshipsDialog
        {...restoreDialogOpener}
        onClose={restoreDialogOpener.close}
        onRestore={handleRestoreRelationship}
      />
      <SwitchRelationshipParentsDialog
        relationship={relationshipState.relationshipData}
        {...switchParentsDialogOpener}
        onClose={switchParentsDialogOpener.close}
        onOk={handleSwitchParents}
      />
    </>
  )
}

function CoupleRelationship({ relationshipState, relationshipActions }) {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const removeDialogOpener = useOverlay()
  const restoreDialogOpener = useOverlay()
  const switchSpousesDialogOpener = useOverlay()

  const { isRole } = useContext(PermissionsContext)
  const isTraditionalCouple = (person1, person2) => {
    return person1.gender === 'MALE' && person2.gender === 'FEMALE'
  }
  const allowSwitchingPersons = (person1, person2) => {
    return isRole(['Data Admin 6']) && !isTraditionalCouple(person1, person2)
  }

  const adviceToAllowSwitchingParents = (person1, person2) => {
    return isTraditionalCouple(person1, person2)
      ? t('person.relationships.cannotSwitch', 'You cannot switch if person1 is male and person2 is female')
      : ''
  }
  const handleRemoveRelationship = (reason) => {
    removeRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        removeDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        removeDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleSwitchSpouses = (reason) => {
    switchPersons(
      'couple',
      relationshipState.relationshipData.relationshipId,
      reason,
      relationshipState.relationshipData.spouse2.personId,
      relationshipState.relationshipData.spouse1.personId
    )
      .then(() => {
        switchSpousesDialogOpener.close()
        window.location.reload()
      })
      .catch((err) => {
        switchSpousesDialogOpener.close()
        error(
          t('person.relationships.errors.switchingPerson', {
            defaultValue: `Error switching persons in the relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  const handleRestoreRelationship = (reason) => {
    restoreRelationship(
      relationshipState.relationshipData.relationshipId,
      relationshipActions.getRelationshipType(),
      reason
    )
      .then(() => {
        restoreDialogOpener.close()
        relationshipActions.fetchCurrentRelationship()
      })
      .catch((err) => {
        restoreDialogOpener.close()
        error(
          t('person.relationships.errors.removingRelationship', {
            defaultValue: `Error removing a relationship: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })
  }

  return (
    <>
      <Row>
        <H2 size="H6" growX supplementary>
          {t(`relationship.type.couple`, `Relationship: Couple`)}
          <DotSeparator />
          <DmcCopyToClipboardTooltip value={relationshipState.relationshipData.relationshipId} />
          {relationshipState.relationshipData.tombstoned && (
            <>
              <DotSeparator />
              {t('tombstoned', 'Tombstoned')}
            </>
          )}
        </H2>
        {isRole('Data Admin 6') && (
          <Button
            emphasis="medium"
            onClick={switchSpousesDialogOpener.handleClick}
            disabled={
              !allowSwitchingPersons(
                relationshipState.relationshipData.spouse1,
                relationshipState.relationshipData.spouse2
              )
            }
            title={adviceToAllowSwitchingParents(
              relationshipState.relationshipData.spouse1,
              relationshipState.relationshipData.spouse2
            )}
          >
            {t('relationship.action.switchSpouses', 'Switch Spouses')}
          </Button>
        )}
        {relationshipState.relationshipData.tombstoned && (
          <Button
            emphasis="medium"
            onClick={restoreDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.restore', 'Restore')}
          </Button>
        )}
        {!relationshipState.relationshipData.tombstoned && (
          <Button
            emphasis="medium"
            onClick={removeDialogOpener.handleClick}
            disabled={!isAuthorized('FtWritePermission')}
          >
            {t('action.tombstone', 'Tombstone')}
          </Button>
        )}
      </Row>
      <Separator size="xs" />
      <RelationshipPersonSummary person={relationshipState.relationshipData.spouse1} role={t('spouse', 'Spouse')} />
      <Separator size="sm" />
      <RelationshipPersonSummary person={relationshipState.relationshipData.spouse2} role={t('spouse', 'Spouse')} />
      <Separator size="sm" />
      <RelationshipFactsSummary relationshipState={relationshipState} relationshipActions={relationshipActions} />
      <RelationshipOrdinancesSummary relationshipState={relationshipState} />
      <ConfirmRemoveDialog
        {...removeDialogOpener}
        onClose={removeDialogOpener.close}
        onRemove={handleRemoveRelationship}
      />
      <RestoreRelationshipsDialog
        {...restoreDialogOpener}
        onClose={restoreDialogOpener.close}
        onRestore={handleRestoreRelationship}
      />
      <SwitchRelationshipSpousesDialog
        relationship={relationshipState.relationshipData}
        {...switchSpousesDialogOpener}
        onClose={switchSpousesDialogOpener.close}
        onOk={handleSwitchSpouses}
      />
    </>
  )
}

function RelationshipFactsSummary({ relationshipState, relationshipActions }) {
  const { t } = useTranslation()
  return (
    <Grid>
      <Cell columns={12}>
        <H3 size="H5" supplementary>
          {t('facts', 'Facts')}
        </H3>
      </Cell>
      {relationshipState.relationshipData.facts &&
        relationshipState.relationshipData.facts.map((fact) => (
          <Cell columns={12} key={fact.conclusionId}>
            <DmcConclusionFact
              factConclusion={fact}
              factOptions={relationshipState.relationshipData.factTypes}
              associationOptions={relationshipState.relationshipData.associationTypes}
              onSaveCallback={(factConclusion, changeMessage) =>
                relationshipActions.updateRelationshipFact(factConclusion, changeMessage)
              }
              onDeleteCallback={(factConclusion, changeMessage) =>
                relationshipActions.deleteRelationshipFact(factConclusion, changeMessage)
              }
            />
          </Cell>
        ))}
      {/* Row */}
      <Cell columns={12}>
        <DmcNewConclusionFact
          factOptions={relationshipState.relationshipData.factTypes}
          associationOptions={relationshipState.relationshipData.associationTypes}
          onCreateCallback={(newConclusionFact) => relationshipActions.createRelationshipFact(newConclusionFact)}
        />
      </Cell>
    </Grid>
  )
}

function RelationshipOrdinancesSummary({ relationshipState }) {
  const { t } = useTranslation()
  const ctrIds = relationshipState.relationshipData.ordinancesCtrIds

  if (!ctrIds || ctrIds.length === 0 || ctrIds[0] === null) {
    return null
  }

  const ctrData = ctrIds.map((ctrId) => ({
    label: t('ctr.label', 'CTR'),
    detail: <CtrLink ctrId={ctrId}>{ctrId}</CtrLink>,
  }))

  return (
    <>
      <Separator size="sm" />
      <H3 size="H5" supplementary>
        {t('ordinances.label', 'Ordinances')}
      </H3>
      <Separator size="sm" />
      <DataView data={ctrData} />
    </>
  )
}

function RelationshipPersonSummary({ person, role }) {
  const { t } = useTranslation()

  return (
    <>
      {person?.personId && (
        <DataView
          data={[
            { label: t('role.label', 'Role'), detail: role },
            { label: t('name', 'Name'), detail: person.name },
            { label: t('lifespan', 'Lifespan'), detail: person.lifespan },
            { label: t('gender.label', 'Gender'), detail: t(`gender.${person.gender}`, person.gender) },
            { label: t('relationship.id', 'ID'), detail: <PersonLink personId={person.personId} /> },
            { label: '', detail: '' },
          ]}
        />
      )}
    </>
  )
}

function RelationshipPersonSummaryWithButtons({
  person,
  role,
  isAuthorized,
  removeButtonLabel,
  removeOnClick,
  changeButtonLabel,
  changeButtonClick,
}) {
  const { t } = useTranslation()

  return (
    <>
      {person?.personId && (
        <DataView
          data={[
            { label: t('role.label', 'Role'), detail: role },
            { label: t('name', 'Name'), detail: person.name },
            { label: t('lifespan', 'Lifespan'), detail: person.lifespan },
            {
              label: '',
              detail: (
                <Button
                  id={`${changeButtonLabel}_btn`}
                  emphasis="low"
                  onClick={changeButtonClick}
                  disabled={!isAuthorized}
                >
                  {changeButtonLabel}
                </Button>
              ),
            },
            { label: t('gender.label', 'Gender'), detail: t(`gender.${person.gender}`, person.gender) },
            { label: t('relationship.id', 'ID'), detail: <PersonLink personId={person.personId} /> },
            { label: '', detail: '' },
            {
              label: '',
              detail: (
                <Button id={`${removeButtonLabel}_btn`} emphasis="low" onClick={removeOnClick} disabled={!isAuthorized}>
                  {removeButtonLabel}
                </Button>
              ),
            },
          ]}
          columns={4}
        />
      )}
    </>
  )
}
